import { ErrorHandler, Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { UiStore } from '../../stores/ui.store';
import { LogUtilityService } from '../logging/log-utility.service';

const STALE_CHUNK_RELOAD_KEY = 'adminportal:stale-chunk-reload-at';
const STALE_CHUNK_RELOAD_DEBOUNCE_MS = 15_000;
const STALE_CHUNK_MESSAGE_PATTERN =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i;

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly uiStore = inject(UiStore);
  private readonly logUtility = inject(LogUtilityService);
  private readonly document = inject(DOCUMENT);

  handleError(error: unknown): void {
    // Unwrap promise rejections
    const resolved = (error as { rejection?: unknown })?.rejection ?? error;

    if (this.isStaleChunkError(resolved)) {
      this.recoverFromStaleChunk(resolved);
      return;
    }

    // 401s are handled by the auth interceptor — skip here
    if (resolved instanceof HttpErrorResponse) {
      if (resolved.status === 401) {
        return;
      }

      let message: string;

      switch (resolved.status) {
        case 403:
          message = "You don't have permission to do that.";
          break;
        case 404:
          message = 'The requested resource was not found.';
          break;
        case 0:
          message = 'Network error — please check your connection.';
          break;
        default:
          message = resolved.status >= 500
            ? 'A server error occurred. Please try again.'
            : `Request failed (${resolved.status}).`;
          break;
      }

      const logMessage = `[HTTP ${resolved.status}] ${resolved.url}`;
      if (resolved.status === 0 || resolved.status >= 500) {
        this.logUtility.logError(logMessage, resolved);
      } else {
        this.logUtility.logWarning(logMessage, { url: resolved.url, status: resolved.status });
      }

      this.uiStore.showToast(message, 'error');
      return;
    }

    this.logUtility.logError('[Unhandled Error]', resolved);
    this.uiStore.showToast('An unexpected error occurred.', 'error');
  }

  private isStaleChunkError(error: unknown): boolean {
    const message = (error as { message?: unknown })?.message;
    return typeof message === 'string' && STALE_CHUNK_MESSAGE_PATTERN.test(message);
  }

  private recoverFromStaleChunk(error: unknown): void {
    const view = this.document.defaultView;
    if (!view) {
      return;
    }

    const now = Date.now();
    const lastReloadAt = Number(view.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY));
    const reloadedRecently =
      Number.isFinite(lastReloadAt) &&
      lastReloadAt > 0 &&
      now - lastReloadAt < STALE_CHUNK_RELOAD_DEBOUNCE_MS;

    if (reloadedRecently) {
      this.logUtility.logError('[Stale Chunk] Reload did not resolve the missing module', error);
      this.uiStore.showToast(
        'A new version of the app is available. Please refresh the page.',
        'error',
      );
      return;
    }

    this.logUtility.logWarning('[Stale Chunk] Reloading to fetch the latest build', {
      message: (error as { message?: string })?.message,
    });
    view.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(now));
    view.location.reload();
  }
}
